import { useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Tag,
  Tooltip,
} from "antd";
import {
  CarOutlined,
  DashboardOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  getAdversaireValidationSchema,
} from "../../../components/declaration/form-validations/schemaValidations";
import AddAdversaire from "../modals/AddAdversaire";

// Refonte UI V2 (variant="v2") : identité navy PoolIns, sections uniformes, sans dégradés multicolores.
const ADV_V2_CSS = `
.adv-v2{--navy:#242166;--ink:#181A2C;--muted:#636A85;--faint:#9BA0B8;--line:#E7E8F2;--line-2:#EFF0F7;
  --ok:#0E9F6E;--warn:#C77A08;--crit:#DC2626;--info:#1D4FD8;--navy-tint:#EDECF8;
  --shadow:0 1px 2px rgba(24,26,44,.04),0 6px 20px -10px rgba(24,26,44,.14)}
.adv-v2 .advc{background:#fff;border:1px solid var(--line);border-radius:14px;box-shadow:var(--shadow);
  height:100%;display:flex;flex-direction:column;transition:border-color .12s,box-shadow .12s}
.adv-v2 .advc:hover{border-color:#CFD2E6;box-shadow:0 12px 30px -14px rgba(24,26,44,.3)}
.adv-v2 .advc-top{display:flex;align-items:flex-start;gap:12px;padding:16px 16px 12px}
.adv-v2 .advc-av{width:46px;height:46px;border-radius:12px;flex-shrink:0;display:grid;place-items:center;
  font-size:20px;background:var(--navy-tint);color:var(--navy)}
.adv-v2 .advc-av.pieton{background:#E4F5EE;color:var(--ok)}
.adv-v2 .advc-id{flex:1;min-width:0}
.adv-v2 .advc-nom{font-size:15px;font-weight:700;color:var(--ink);letter-spacing:-.01em;line-height:1.2}
.adv-v2 .advc-tags{display:flex;gap:6px;flex-wrap:wrap;margin-top:7px}
.adv-v2 .ap{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:650;padding:3px 10px;border-radius:999px;line-height:1;white-space:nowrap}
.adv-v2 .ap .d{width:6px;height:6px;border-radius:50%;background:currentColor}
.adv-v2 .ap.navy{color:var(--navy);background:var(--navy-tint)}
.adv-v2 .ap.ok{color:#0B7C55;background:#E4F5EE}
.adv-v2 .ap.warn{color:#9A5E06;background:#FBF0DC}
.adv-v2 .ap.crit{color:#B91C1C;background:#FBE7E7}
.adv-v2 .ap.info{color:#1D4FD8;background:#E6EDFD}
.adv-v2 .advc-body{padding:0 16px 16px;display:flex;flex-direction:column;gap:12px}
.adv-v2 .advc-sec .sl{font-size:10.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:var(--faint);margin-bottom:6px}
.adv-v2 .advc-box{background:#FAFAFE;border:1px solid var(--line-2);border-radius:10px;padding:11px 12px}
.adv-v2 .kv{display:flex;justify-content:space-between;gap:10px;font-size:13px;padding:2px 0}
.adv-v2 .kv .k{color:var(--muted)}
.adv-v2 .kv .v{color:var(--ink);font-weight:600;text-align:right}
.adv-v2 .immat{font-family:ui-monospace,Menlo,monospace;font-weight:700;font-size:15px;color:var(--navy);letter-spacing:.04em}
.adv-v2 .achip{display:inline-flex;align-items:center;gap:5px;font-size:11.5px;font-weight:600;color:var(--muted);border:1px solid var(--line);border-radius:999px;padding:2px 9px;background:#fff;margin-right:6px}
.adv-v2 .trajet{display:flex;align-items:center;gap:8px;font-size:13px;color:var(--ink);font-weight:600;flex-wrap:wrap}
.adv-v2 .trajet .arr{color:var(--faint)}
.adv-v2 .dom{font-size:12.5px;color:#4b5563}
.adv-v2 .advc-foot{margin-top:auto;display:flex;gap:8px;padding:12px 16px;border-top:1px solid var(--line-2)}
.adv-v2 .advc-btn{flex:1;border:1px solid var(--line);background:#fff;border-radius:9px;padding:8px;font-size:13px;font-weight:600;cursor:pointer;color:var(--ink)}
.adv-v2 .advc-btn:hover{background:#F7F8FC}
.adv-v2 .advc-btn.danger{color:var(--crit);border-color:#F2C7C7}
.adv-v2 .advc-btn.danger:hover{background:#FBE7E7}
`;

const AdversairesTab = ({ sinistre, victimes, setRefetch, variant }) => {
  const isV2 = variant === "v2";
  const readOnly = !!sinistre?.lectureSeule;
  const [isPieton, setIsPieton] = useState(null);
  const [wasAdversaireConducteur, setWasAdversaireConducteur] = useState(true);
  const [currentAdversaire, setCurrentAdversaire] = useState(null);
  const [isNewAdversaireModalVisible, setIsNewAdversaireModalVisible] =
    useState(false);

  const adversaires = useMemo(
    () => victimes?.filter((victime) => victime.adversaire === true) || [],
    [victimes]
  );


  const adversaireInitialValue = {
    items: [
      {
        pieton: "non",
        nom: "",
        prenom: "",
        telephone: "",
        profession: "",
        adresse: "",
        engin: {
          marque: "",
          puissance: "",
          immatriculation: "",
          usage: "PRIVE",
        },
        nombrePersonneTransportees: 0,
        conducteur: {
          nom: "",
          prenom: "",
          telephone: "",
          numeroPermis: "",
          categoriePermis: "B",
          dateDelivrance: null,
          lieuDelivrance: "",
          dateExpiration: null,
          qualite: "",
        },
        societeAssurance: "",
        typeSinistre: "",
        conduisait: "oui",
        provenance: "",
        destination: "",
        vitesse: 0,
        typeAdversaire: "",
      },
    ],
  };

  const adversaireFormik = useFormik({
    initialValues: adversaireInitialValue,
    validationSchema: getAdversaireValidationSchema(
      isPieton,
      wasAdversaireConducteur
    ),
  });

  const handleNewAdversaireOpen = () => {
    if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
    setCurrentAdversaire(null);
    setIsNewAdversaireModalVisible(true);
  };

  const handleNewAdversaireClose = () => {
    setIsNewAdversaireModalVisible(false);
    setIsPieton(null);
    adversaireFormik.resetForm();
    setWasAdversaireConducteur(true);
    setCurrentAdversaire(null);
  };


  const getTypeSinistreColor = (type) => {
    const colors = {
      MIXTE: 'red',
      MATERIEL: 'orange',
      CORPOREL: 'volcano'
    };
    return colors[type] || 'default';
  };

  const showModal = (adversaire) => {
    if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
    setIsNewAdversaireModalVisible(true);
    setCurrentAdversaire(adversaire);
  };

  const handleDelete = (adv) => {
    if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
    console.log(adv);
  }

  const getCardBorderColor = (type) => {
    const colors = {
      MATERIEL: '#fa8c16',
      CORPOREL: '#ff7a45',
      MIXTE: '#722ed1',
    };
    return colors[type] || '#1890ff';
  };

  const getTypeSinistreGradient = (type) => {
    const gradients = {
      MIXTE: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
      MATERIEL: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
      CORPOREL: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)'
    };
    return gradients[type] || 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)';
  };


  return (
    <div
      className={isV2 ? "adv-v2" : ""}
      style={isV2 ? { padding: 0 } : { padding: '24px', background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)', minHeight: '100vh' }}
    >
      {isV2 && <style>{ADV_V2_CSS}</style>}
      <div style={isV2 ? {} : { maxWidth: '1600px', margin: '0 auto' }}>
        {/* En-tête : en V2, réduit à une barre d'action (le cockpit fournit déjà le contexte) */}
        {isV2 ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleNewAdversaireOpen}
              disabled={sinistre?.sortDossier === "S" || sinistre?.sortDossier === "T" || readOnly}
              style={{ background: '#242166', border: 'none', borderRadius: 10, fontWeight: 600 }}>
              Nouvelle partie adverse
            </Button>
          </div>
        ) : (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '10px 24px',
          marginBottom: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <Row gutter={24} align="middle">
            <Col xs={24} sm={24} md={12} lg={16}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}>
                  <TeamOutlined style={{ fontSize: '28px', color: 'white' }} />
                </div>
                <div>
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: '0 0 4px 0'
                  }}>
                    Parties Adverses
                  </h2>
                  <p style={{
                    fontSize: '14px',
                    color: '#64748b',
                    margin: 0
                  }}>
                    Gestion des parties adverses impliquées dans le sinistre
                  </p>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
                <Badge
                  count={adversaires.length}
                  showZero
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                    fontSize: '16px',
                    height: '24px',
                    minWidth: '28px',
                    lineHeight: '20px',
                    marginBottom: '40px!important'
                  }}
                >
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#475569',
                    marginRight: '35px'
                  }}>
                    Total
                  </span>
                </Badge>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={handleNewAdversaireOpen}
                  disabled={sinistre?.sortDossier === "S" || sinistre?.sortDossier === "T" || readOnly}
                  style={{
                    background: '#242166',
                    border: 'none',
                    height: '35px',
                    padding: '0 12px',
                    fontSize: '15px',
                    fontWeight: '600',
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                  }}
                >
                  Nouvelle Partie Adverse
                </Button>
              </div>
            </Col>
          </Row>
        </div>
        )}

        {/* Grille des adversaires */}
        {adversaires.length ? (
          <Row gutter={[24, 24]}>
            {adversaires.map((adversaire) => (
              isV2 ? (
              <Col xs={24} sm={24} md={12} lg={8} key={adversaire.codeId}>
                <div className="advc">
                  <div className="advc-top">
                    <span className={`advc-av ${adversaire.engin ? "" : "pieton"}`}>
                      {adversaire.engin ? <CarOutlined /> : <UserOutlined />}
                    </span>
                    <div className="advc-id">
                      <div className="advc-nom">{`${adversaire?.personne?.prenom || ""} ${adversaire?.personne?.nom || ""}`.trim() || "Partie adverse"}</div>
                      <div className="advc-tags">
                        {adversaire.typeSinistre && <span className="ap crit"><span className="d"></span>{adversaire.typeSinistre}</span>}
                        <span className={`ap ${adversaire.engin ? "navy" : "ok"}`}>{adversaire.engin ? "Véhiculé" : "Piéton"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="advc-body">
                    {(adversaire?.personne?.telephone || adversaire?.personne?.email) && (
                      <div className="advc-sec">
                        <div className="sl">Contact</div>
                        <div className="advc-box">
                          {adversaire?.personne?.telephone && <div className="kv"><span className="k">Téléphone</span><span className="v">{adversaire.personne.telephone}</span></div>}
                          {adversaire?.personne?.email && <div className="kv"><span className="k">Email</span><span className="v">{adversaire.personne.email}</span></div>}
                        </div>
                      </div>
                    )}
                    {adversaire.engin && (
                      <div className="advc-sec">
                        <div className="sl">Véhicule</div>
                        <div className="advc-box">
                          <div className="immat">{adversaire.engin.immatriculation || "—"}</div>
                          <div style={{ fontSize: 13, color: "#181A2C", fontWeight: 600, margin: "4px 0 8px" }}>{`${adversaire.engin.marque || ""} ${adversaire.engin.modele || ""}`.trim() || "—"}</div>
                          {adversaire.engin.couleur && <span className="achip">{adversaire.engin.couleur}</span>}
                          {adversaire.vitesse && <span className="achip"><DashboardOutlined /> {adversaire.vitesse} km/h</span>}
                        </div>
                      </div>
                    )}
                    {adversaire.driver && (
                      <div className="advc-sec">
                        <div className="sl">Conducteur</div>
                        <div className="advc-box">
                          <div className="kv"><span className="k">Nom</span><span className="v">{`${adversaire.driver.prenom || ""} ${adversaire.driver.nom || ""}`.trim() || "—"}</span></div>
                          {adversaire.driver.telephone && <div className="kv"><span className="k">Téléphone</span><span className="v">{adversaire.driver.telephone}</span></div>}
                          {adversaire.driver.numeroPermis && <div className="kv"><span className="k">Permis</span><span className="v">{adversaire.driver.numeroPermis}</span></div>}
                        </div>
                      </div>
                    )}
                    {(adversaire.provenance || adversaire.destination) && (
                      <div className="advc-sec">
                        <div className="sl">Trajet</div>
                        <div className="advc-box">
                          <div className="trajet">{adversaire.provenance || "?"} <span className="arr">→</span> {adversaire.destination || "?"}</div>
                          {adversaire.nombrePersonneTransportees > 0 && <div style={{ marginTop: 6 }}><span className="achip">{adversaire.nombrePersonneTransportees} personne(s) transportée(s)</span></div>}
                        </div>
                      </div>
                    )}
                    {adversaire.descriptionDegatsVictime && (
                      <div className="advc-sec">
                        <div className="sl">Dommages</div>
                        <div className="advc-box dom">{adversaire.descriptionDegatsVictime}</div>
                      </div>
                    )}
                  </div>
                  <div className="advc-foot">
                    <button className="advc-btn" disabled={readOnly} onClick={() => showModal(adversaire)}><EditOutlined /> Modifier</button>
                    <button className="advc-btn danger" disabled={readOnly} onClick={() => handleDelete(adversaire.codeId)}><DeleteOutlined /> Supprimer</button>
                  </div>
                </div>
              </Col>
              ) : (
              <Col xs={24} sm={24} md={12} lg={8} key={adversaire.codeId}>
                <Card
                  style={{
                    height: '100%',
                    borderRadius: '16px',
                    border: 'none',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                  }}
                  styles={{ body: { padding: 0 } }}
                >
                  {/* Barre de couleur en haut */}
                  <div style={{
                    height: '6px',
                    background: getTypeSinistreGradient(adversaire.typeSinistre)
                  }} />

                  {/* Contenu de la carte */}
                  <div style={{ padding: '24px' }}>
                    {/* En-tête avec avatar et info */}
                    <div style={{ marginBottom: '20px' }}>
                      <Row justify="space-between" align="top">
                        <Col span={18}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <Avatar
                              size={64}
                              icon={adversaire.engin ? <CarOutlined /> : <UserOutlined />}
                              style={{
                                background: adversaire.engin
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                flexShrink: 0
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h3 style={{
                                fontSize: '18px',
                                fontWeight: '700',
                                color: '#1e293b',
                                margin: '0 0 6px 0',
                                lineHeight: '1.3',
                                wordBreak: 'break-word'
                              }}>
                                {adversaire.personne.prenom} {adversaire.personne.nom}
                              </h3>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <Tag
                                  icon={<IdcardOutlined />}
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: '#f1f5f9',
                                    color: '#475569'
                                  }}
                                >
                                  {adversaire.codeId}
                                </Tag>
                                <Tag
                                  color={adversaire.engin ? 'blue' : 'green'}
                                  icon={adversaire.engin ? <CarOutlined /> : <UserOutlined />}
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    border: 'none'
                                  }}
                                >
                                  {adversaire.engin ? 'Véhiculé' : 'Piéton'}
                                </Tag>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col span={6} style={{ textAlign: 'right' }}>
                          <Tag
                            style={{
                              fontSize: '11px',
                              fontWeight: '700',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              background: getTypeSinistreGradient(adversaire.typeSinistre),
                              color: 'white',
                              border: 'none',
                              boxShadow: `0 2px 8px ${getTypeSinistreColor(adversaire.typeSinistre)}40`
                            }}
                          >
                            {adversaire.typeSinistre}
                          </Tag>
                        </Col>
                      </Row>
                    </div>

                    {/* Informations de contact */}
                    <div style={{
                      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      marginBottom: '16px',
                      border: '1px solid #7dd3fc'
                    }}>
                      {adversaire.personne.telephone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: adversaire.personne.email ? '8px' : '0' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <PhoneOutlined style={{ color: '#0284c7', fontSize: '16px' }} />
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e', flex: 1 }}>
                            {adversaire.personne.telephone}
                          </span>
                        </div>
                      )}
                      {adversaire.personne.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <MailOutlined style={{ color: '#0284c7', fontSize: '16px' }} />
                          </div>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#0c4a6e',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {adversaire.personne.email}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Informations du véhicule */}
                    {adversaire.engin && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '10px'
                        }}>
                          <div style={{
                            width: '4px',
                            height: '16px',
                            borderRadius: '2px',
                            background: 'linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)'
                          }} />
                          <span style={{
                            fontSize: '12px',
                            color: '#64748b',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Véhicule
                          </span>
                        </div>
                        <div style={{
                          background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                          borderRadius: '12px',
                          padding: '16px',
                          border: '2px solid #3b82f6'
                        }}>
                          <div style={{
                            fontSize: '20px',
                            fontWeight: '800',
                            color: '#1e40af',
                            marginBottom: '8px',
                            letterSpacing: '1px',
                            fontFamily: 'monospace'
                          }}>
                            {adversaire.engin.immatriculation}
                          </div>
                          <div style={{
                            fontSize: '15px',
                            color: '#1e40af',
                            fontWeight: '600',
                            marginBottom: '6px'
                          }}>
                            {adversaire.engin.marque} {adversaire.engin.modele}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {adversaire.engin.couleur && (
                              <Tag style={{
                                background: 'white',
                                border: '1px solid #3b82f6',
                                color: '#1e40af',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}>
                                {adversaire.engin.couleur}
                              </Tag>
                            )}
                            {adversaire.vitesse && (
                              <Tag
                                icon={<DashboardOutlined />}
                                style={{
                                  background: 'white',
                                  border: '1px solid #3b82f6',
                                  color: '#1e40af',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}
                              >
                                {adversaire.vitesse} km/h
                              </Tag>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Informations du conducteur */}
                    {adversaire.driver && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '10px'
                        }}>
                          <div style={{
                            width: '4px',
                            height: '16px',
                            borderRadius: '2px',
                            background: 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)'
                          }} />
                          <span style={{
                            fontSize: '12px',
                            color: '#64748b',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Conducteur
                          </span>
                        </div>
                        <div style={{
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          borderRadius: '12px',
                          padding: '14px',
                          border: '2px solid #f59e0b'
                        }}>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '700',
                            color: '#92400e',
                            marginBottom: '6px'
                          }}>
                            {adversaire.driver.prenom} {adversaire.driver.nom}
                          </div>
                          {adversaire.driver.telephone && (
                            <div style={{
                              fontSize: '13px',
                              color: '#b45309',
                              marginBottom: '6px',
                              fontWeight: '500'
                            }}>
                              📞 {adversaire.driver.telephone}
                            </div>
                          )}
                          {adversaire.driver.numeroPermis && (
                            <Tag style={{
                              background: 'white',
                              border: '1px solid #f59e0b',
                              color: '#92400e',
                              fontSize: '11px',
                              fontWeight: '600',
                              borderRadius: '6px'
                            }}>
                              Permis: {adversaire.driver.numeroPermis}
                            </Tag>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Trajet */}
                    {(adversaire.provenance || adversaire.destination) && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '10px'
                        }}>
                          <div style={{
                            width: '4px',
                            height: '16px',
                            borderRadius: '2px',
                            background: 'linear-gradient(180deg, #a855f7 0%, #9333ea 100%)'
                          }} />
                          <span style={{
                            fontSize: '12px',
                            color: '#64748b',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Trajet
                          </span>
                        </div>
                        <div style={{
                          background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                          padding: '14px',
                          borderRadius: '12px',
                          border: '2px solid #a855f7'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: adversaire.nombrePersonneTransportees ? '10px' : '0'
                          }}>
                            <EnvironmentOutlined style={{ fontSize: '18px', color: '#7c3aed' }} />
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#6b21a8'
                              }}>
                                {adversaire.provenance || '?'}
                              </span>
                              <span style={{ fontSize: '16px', color: '#a855f7' }}>→</span>
                              <span style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#6b21a8'
                              }}>
                                {adversaire.destination || '?'}
                              </span>
                            </div>
                          </div>
                          {adversaire.nombrePersonneTransportees > 0 && (
                            <Tag
                              icon={<TeamOutlined />}
                              style={{
                                background: 'white',
                                border: '1px solid #a855f7',
                                color: '#6b21a8',
                                fontSize: '12px',
                                fontWeight: '600',
                                borderRadius: '6px'
                              }}
                            >
                              {adversaire.nombrePersonneTransportees} personne(s) transportée(s)
                            </Tag>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Description des dégâts */}
                    {adversaire.descriptionDegatsVictime && (
                      <div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '10px'
                        }}>
                          <div style={{
                            width: '4px',
                            height: '16px',
                            borderRadius: '2px',
                            background: 'linear-gradient(180deg, #ef4444 0%, #dc2626 100%)'
                          }} />
                          <span style={{
                            fontSize: '12px',
                            color: '#64748b',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Dégâts Constatés
                          </span>
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: '#7f1d1d',
                          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                          padding: '14px',
                          borderRadius: '10px',
                          borderLeft: '4px solid #ef4444',
                          lineHeight: '1.6',
                          fontWeight: '500'
                        }}>
                          <WarningOutlined style={{ marginRight: '8px', color: '#dc2626' }} />
                          {adversaire.descriptionDegatsVictime}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions en bas de carte */}
                  <div style={{
                    borderTop: '1px solid #e2e8f0',
                    // padding: '16px 24px',
                    background: '#f8fafc',
                    display: 'flex',
                    justifyContent: 'space-around',
                    gap: '8px',
                    marginTop: '0',
                  }}>
                    <Button
                      type="text"
                      icon={<EditOutlined style={{ fontSize: '16px' }} />}
                      disabled={readOnly}
                      onClick={() => showModal(adversaire)}
                      style={{
                        flex: 1,
                        fontWeight: '600',
                        color: '#3b82f6',
                        height: '40px',
                        borderRadius: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#eff6ff';
                        e.currentTarget.style.color = '#1e40af';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#3b82f6';
                      }}
                    >
                      Modifier
                    </Button>
                    <Button
                      type="text"
                      icon={<DeleteOutlined style={{ fontSize: '16px' }} />}
                      disabled={readOnly}
                      onClick={() => handleDelete(adversaire.codeId)}
                      style={{
                        flex: 1,
                        fontWeight: '600',
                        color: '#ef4444',
                        height: '40px',
                        borderRadius: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fef2f2';
                        e.currentTarget.style.color = '#dc2626';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#ef4444';
                      }}
                    >
                      Supprimer
                    </Button>
                  </div>
                </Card>
              </Col>
              )
            ))}
          </Row>
        ) : (
          <Card style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            padding: '40px'
          }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <p style={{
                    fontSize: '16px',
                    color: '#64748b',
                    fontWeight: '500',
                    margin: '16px 0 8px 0'
                  }}>
                    Aucune partie adverse enregistrée
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#94a3b8',
                    margin: 0
                  }}>
                    Cliquez sur le bouton ci-dessus pour ajouter une partie adverse
                  </p>
                </div>
              }
            />
          </Card>
        )}
        <AddAdversaire
          open={isNewAdversaireModalVisible}
          adversaire={currentAdversaire}
          setOpen={setIsNewAdversaireModalVisible}
          sinistre={sinistre}
          onClose={handleNewAdversaireClose}
          onSaved={() => {
            if (typeof setRefetch === "function") {
              setRefetch((prev) => !prev);
            }
          }}
        // edit={editMode}
        />
      </div>
    </div>
  );
};

export default AdversairesTab;
